
import { useEffect, useRef } from 'react';
import { visualLog } from '@/components/debug/VisualDebugger';

/**
 * Hook per gestire l'altezza dinamica del viewport considerando la tastiera virtuale
 * Aggiorna la variabile CSS --viewport-height in tempo reale
 */
export const useViewportHeight = () => {
  const baseHeightRef = useRef<number>(0);
  const lastHeightRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const logCssVariables = () => {
      const styles = getComputedStyle(document.documentElement);
      const viewportVar = styles.getPropertyValue('--viewport-height').trim();
      const keyboardVar = styles.getPropertyValue('--keyboard-offset').trim();
      visualLog.info(`--viewport-height: ${viewportVar || 'not set'}`);
      visualLog.info(`--keyboard-offset: ${keyboardVar || 'not set'}`);
    };

    const mediaQueries = ['(orientation: portrait)', '(prefers-color-scheme: dark)'];
    const mqlListeners: Array<() => void> = [];
    mediaQueries.forEach(q => {
      const mql = window.matchMedia(q);
      const logStatus = () => visualLog.info(`matchMedia ${q}: ${mql.matches}`);
      logStatus();
      const add = (mql.addEventListener ?? mql.addListener).bind(mql);
      const remove = (mql.removeEventListener ?? mql.removeListener).bind(mql);
      add('change', logStatus);
      mqlListeners.push(() => remove('change', logStatus));
    });

    const updateViewportHeight = () => {
      // Clear timeout precedente per evitare aggiornamenti troppo frequenti
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        const vh = window.innerHeight;
        const dvh = window.visualViewport?.height ?? vh;
        const diff = vh - dvh;
        visualLog.info(`100vh: ${vh}px - 100dvh: ${dvh}px (diff ${diff}px)`);
        
        // Memorizza l'altezza base al primo rilevamento
        if (baseHeightRef.current === 0) {
          baseHeightRef.current = vh;
          lastHeightRef.current = vh;
          document.documentElement.style.setProperty('--viewport-height', `${vh}px`);
          visualLog.info(`Viewport height initialized: ${vh}px`);
          logCssVariables();
          window.dispatchEvent(
            new CustomEvent('viewport-height-changed', { detail: { height: vh } })
          );
          return;
        }

        // Evita aggiornamenti inutili
        if (Math.abs(vh - lastHeightRef.current) < 10) {
          lastHeightRef.current = vh;
          return;
        }

        lastHeightRef.current = vh;

        // Aggiorna solo se l'altezza aumenta (es. cambio orientamento)
        if (vh > baseHeightRef.current) {
          baseHeightRef.current = vh;
          document.documentElement.style.setProperty('--viewport-height', `${vh}px`);
          visualLog.info(`Viewport height updated: ${vh}px`);
          logCssVariables();
          window.dispatchEvent(
            new CustomEvent('viewport-height-changed', { detail: { height: vh } })
          );
        }
      }, 50); // Debounce di 50ms
    };

    // Inizializza l'altezza
    updateViewportHeight();
    logCssVariables();

    // Ascolta i cambiamenti di dimensione
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);

    // Per dispositivi che supportano Visual Viewport API (più preciso)
    if ('visualViewport' in window) {
      const visualViewport = window.visualViewport!;
      const logViewport = () => {
        visualLog.info(
          `visualViewport h:${visualViewport.height} w:${visualViewport.width} offsetTop:${visualViewport.offsetTop}`
        );
      };
      visualViewport.addEventListener('resize', updateViewportHeight);
      visualViewport.addEventListener('resize', logViewport);
      visualViewport.addEventListener('scroll', logViewport);
      
      return () => {
        window.removeEventListener('resize', updateViewportHeight);
        window.removeEventListener('orientationchange', updateViewportHeight);
        visualViewport.removeEventListener('resize', updateViewportHeight);
        visualViewport.removeEventListener('resize', logViewport);
        visualViewport.removeEventListener('scroll', logViewport);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
      mqlListeners.forEach(remove => remove());
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
}
